import {
    previewImagesTypes,
    previewVideoTypes,
    previewOriginFormatTypes,
    previewFormatTypes,
    ThumbLayoutTypes,
    previewFilenameKeys,
} from './PerformanceMarketingConstants';

export const budgetExceededMessage = 'After the top-up, the campaign funds will exceed the declared budget limit. Proceeding will update the budget to prevent unspent funds and adjust the campaign dynamics accordingly. If you do not wish to extend the budget, cancel and modify the top-up amount.';

export const minimumPaymentAmountMessage = 'The amount left to pay after applying your unused funds is less than 1. Increase the top-up amount, or lower it so your unused funds cover it in full.';

export const automaticLocationsDescription = 'To make the most of your budget, Adfound automatically includes only countries where your campaign is likely to perform well.';

export const catalogCampaignTerminationTileNotice = 'Creating this campaign will disable the catalog campaign for this project to prevent attribution overlap and ensure accurate performance measurement.';

export const questions = (contactUrl, digitalServiceTaxLearnMoreUrl, catalogCampaignFeesHelpUrl) =>
    [
        {
            title: 'How does Adfound work?',
            description: 'Adfound helps creators grow their crowdfunding campaigns with smarter ads. It connects directly to ad platforms via the Conversion API (CAPI) for accurate tracking and reaches exclusive, high-intent audiences. Bids, audiences, and ad delivery are automatically optimised, so your campaign gains momentum while you focus on your launch.',
            isCollapsed: true,
        },
        {
            title: 'What should my budget be?',
            description: 'Adfound allows campaigns with a budget of minimum 25 EUR per day. Once you input the basic details into the system, it will guide you and suggest appropriate budgets for the phase you’re in. The final decision regarding budget is yours.',
            isCollapsed: true,
        },
        {
            title: 'Is there a spending limit?',
            description: 'We recommend spending as much as makes sense and for as long as it remains profitable. If you’re satisfied with the ROAS (Return On Advertising Spend), consider increasing your investment. If you notice ROAS decreasing, it might be the right time to reduce spending. Always ensure your budget covers at least a few days of campaign activity.',
            isCollapsed: true,
        },
        {
            title: 'How long the campaign should be?',
            description: 'The system allows you to launch a campaign for as little as one hour. However, it is recommended to run the campaign for at least three days to allow the learning phase to complete and optimize performance.',
            isCollapsed: true,
        },
        {
            title: 'Is there a guaranteed ROAS?',
            description: 'Adfound uses a highly refined audience database to ensure your ads reach the right people. However, whether they choose to support your project depends on how appealing your project is to them. For this reason, we cannot guarantee a specific ROAS for your campaign.',
            isCollapsed: true,
        },
        {
            title: 'What do I need to prepare for the campaign?',
            description: 'Provide Adfound with a selection of graphics and text proposals that reflect your project’s vision. Using these assets, Adfound will create optimized ad variations and manage your campaign for you.',
            isCollapsed: true,
        },
        {
            title: 'What if I want to stop the ads, increase the budget, etc.?',
            description: `<div>You can set spending limits during the initial setup and adjust them at any time.<br><br>
                          You can pause and unpause your ads anytime. Manually pausing the ad will stop its funding.
                          However, spending funds are automatically paused for low-performing assets, so you don't need
                          to worry about doing it yourself.<br><br> If you want to terminate your whole campaign permanently
                          and have your unused funds refunded or transferred to another campaign,
                          <a href=\"${contactUrl}\" target=\"_blank\" rel=\"nofollow noopener noreferrer\">
                          contact us</a>.</div>`,
            isCollapsed: true,
        },
        {
            title: 'How much does it cost to work with Adfound?',
            description: 'Adfound fee is 5% of the money the ads bring. You will be charged at the end of your marketing campaign, based on the total revenue generated through Adfound. Campaigns in the "Upcoming" phase are not charged. You can monitor your spending and fees via the dashboard, with data synchronized periodically. In some cases, applicable taxes, including Digital Service Tax (DST), may also apply depending on your jurisdiction and campaign setup.',
            isCollapsed: true,
        },
        {
            title: 'What is a Digital Service Tax (DST)?',
            description: `A Digital Service Tax (DST) is an extra fee charged in some countries on certain digital services, including online advertising and social media ads. The fee is usually added by the platform or provider and is calculated as a percentage of your ad spend based on where your ads are shown. It typically appears automatically on invoices or ad account charges when targeting users in countries where DST applies. <a href=\"${digitalServiceTaxLearnMoreUrl}\" target=\"_blank\" rel=\"nofollow noopener noreferrer\">Learn more</a>`,
            isCollapsed: true,
        },
        {
            title: 'How do you determine which funds were brought by Adfound?',
            description: 'For both generating interest and revenue for your project, Adfound uses 7-day click and 1-day view attribution from Meta system.',
            isCollapsed: true,
        },
        {
            title: 'Do I need a business account in Meta?',
            description: 'You don’t need to have Meta account to start running ads with Adfound, your ads will be displayed on our established accounts.',
            isCollapsed: true,
        },
        {
            title: 'What are Catalog Campaigns?',
            description: `<div>Catalog Campaigns are a fully managed advertising solution that promotes your eligible projects across
                          advertising platforms. Unlike traditional advertising, you don't pay for ad spend upfront. Adfound covers the
                          entire advertising budget and all campaign risk. You simply enroll once, enable Catalog Campaigns for eligible
                          projects, and Adfound handles campaign creation, optimization, and delivery.<br><br>
                          You only pay when your campaigns generate attributed sales. If a campaign doesn't deliver results, you don't
                          pay for advertising.<br><br>
                          Catalog Campaigns are designed to create additional opportunities for your project, but enabling them doesn't
                          guarantee a specific level of exposure, ad spend, or additional revenue, and won't replace a dedicated
                          campaign.<br><br>
                          <a href=\"${catalogCampaignFeesHelpUrl}\" target=\"_blank\" rel=\"nofollow noopener noreferrer\">
                          Learn more</a> about Catalog Campaign fees and attribution.</div>`,
            isCollapsed: true,
        },
        {
            title: 'Do I need to set up any tracking tools or codes?',
            description: 'You don’t need to connect any tracking codes or tools. Once you launch your campaign, Adfound will handle all the tracking and measurement for you.',
            isCollapsed: true,
        },
        {
            title: 'How do Adfound invoices work?',
            description: `<div>Adfound invoices are generated monthly and become available at the beginning of each month.<br><br>
                          Each month, you'll receive two invoices:
                          <ul>
                              <li>Top-ups invoice — lists all funds added to your Adfound balance while setting up or running campaigns.
                                  These funds are used for campaign spending.</li>
                              <li>Fees &amp; Taxes invoice — lists all Adfound fees and applicable taxes from the previous month.</li>
                          </ul>
                          Top-up invoices are already paid because payment is completed when funds are added to your Adfound balance.
                          You can find all invoices anytime in the Invoices tab. We'll also send you an email reminder when new
                          monthly invoices are available.</div>`,
            isCollapsed: true,
        },
        {
            title: 'What are credits and how do they work?',
            description: 'Credits are free funds granted to your business or campaign. You can use them to run ads, but since you didn’t pay for them, they are not included on invoices or in reports that show only your paid and spent money.',
            isCollapsed: true,
        },
    ];

export const benefits = [
    {
        title: 'Reach the right audience',
        icon: 'users',
        description:
            'With years of experience engaging millions of gamers, Adfound guarantees your ads reach the most relevant audience.',
    },
    {
        title: 'Invest Smarter',
        icon: 'money-bill-trend-up',
        description:
            'Adfound constantly optimizes your campaigns, ensuring that funds are allocated only to ads that drive attention and value to your project.',
    },
    {
        title: 'Save time and effort',
        icon: 'watch',
        description:
            'Set up your ads in just a few clicks using an advertising account with a long-standing, trusted reputation.',
    },
    {
        title: 'Maximize Performance',
        icon: 'analytics',
        description:
            'Simply provide your graphics and copy, and Adfound will run automated tests to identify and invest heavily in the highest-performing combinations.',
    },
    {
        title: 'Maintain control',
        icon: 'sliders-h',
        description:
            'Spend only what’s right for your campaign. Pause or boost your ads whenever needed.',
    },

    {
        title: 'Monitor results',
        icon: 'gauge-max',
        description:
            'Track your campaign’s performance with our user-friendly dashboard, offering real-time insights into key metrics.',
    },
];

export const howItWorksInfo = [
    {
        title: 'Adfound uses platform CAPI',
        description: ' Adfound connects directly to ad platforms via Conversion API (CAPI), ensuring more accurate tracking, better optimisation, and stronger performance than pixel-only setups.',
        icon: 'link',
    },
    {
        title: 'Access to exclusive audiences',
        description: 'Adfound campaigns tap into exclusive, high-intent audiences built from years of experience running successful crowdfunding marketing campaigns.',
        icon: 'users',
    },
    {
        title: 'Optimisations happen under the hood',
        description: ' Adfound continuously optimises bids, audiences, and delivery in the background, so creators can focus on their launch while performance improves automatically.',
        icon: 'rocket-launch',
    },
    {
        title: 'Catalog campaigns',
        description: 'Catalog campaigns automatically promote your eligible projects. Instead of managing campaigns yourself, you pay only a commission on attributed sales.',
        icon: 'rectangle-vertical-history',
    },
];

export const previewFormats = {
    FacebookMobileFeed: {
        id: 1,
        icons: ['facebook-f'],
        text: 'mobile feed',
        type: previewImagesTypes.ShortVertical,
        name: previewFormatTypes.FacebookMobileFeed,
        originType: previewOriginFormatTypes.Feeds,
        variants: {
            [ThumbLayoutTypes.Stacked]: {
                type: previewImagesTypes.Square,
                filenameKey: previewFilenameKeys.squareFilename,
            },
            [ThumbLayoutTypes.SideBySide]: {
                type: previewImagesTypes.ShortVertical,
                filenameKey: previewFilenameKeys.shortVerticalFilename,
            },
        },
    },
    InstagramMobileFeed: {
        id: 2,
        icons: ['instagram'],
        text: 'mobile feed',
        type: previewImagesTypes.Square,
        name: previewFormatTypes.InstagramMobileFeed,
        originType: previewOriginFormatTypes.Feeds,
        variants: {
            [ThumbLayoutTypes.Stacked]: {
                type: previewImagesTypes.Square,
                filenameKey: previewFilenameKeys.squareFilename,
            },
            [ThumbLayoutTypes.SideBySide]: {
                type: previewImagesTypes.ShortVertical,
                filenameKey: previewFilenameKeys.shortVerticalFilename,
            },
        },
    },
    MetaReels: {
        id: 3,
        icons: ['facebook-f', 'instagram'],
        text: 'reels',
        type: previewImagesTypes.Vertical,
        name: previewFormatTypes.MetaReels,
        originType: previewOriginFormatTypes.StoriesAndReels,
        variants: {
            [ThumbLayoutTypes.Stacked]: {
                type: previewImagesTypes.Vertical,
                filenameKey: previewFilenameKeys.verticalFilename,
            },
            [ThumbLayoutTypes.SideBySide]: {
                type: previewImagesTypes.Vertical,
                filenameKey: previewFilenameKeys.verticalFilename,
            },
        },
    },
    MetaStories: {
        id: 4,
        icons: ['facebook-f', 'instagram'],
        text: 'stories',
        type: previewImagesTypes.Vertical,
        name: previewFormatTypes.MetaStories,
        originType: previewOriginFormatTypes.StoriesAndReels,
        variants: {
            [ThumbLayoutTypes.Stacked]: {
                type: previewImagesTypes.Vertical,
                filenameKey: previewFilenameKeys.verticalFilename,
            },
            [ThumbLayoutTypes.SideBySide]: {
                type: previewImagesTypes.Vertical,
                filenameKey: previewFilenameKeys.verticalFilename,
            },
        },
    },
    LegacyFacebookDesktopRightColumn: {
        id: 5,
        icons: ['facebook-f'],
        text: 'desktop sidebar',
        type: previewImagesTypes.Horizontal,
        name: previewFormatTypes.LegacyFacebookDesktopRightColumn,
        originType: previewOriginFormatTypes.RightColumn,
        variants: {
            [ThumbLayoutTypes.Stacked]: {
                type: previewImagesTypes.Horizontal,
                filenameKey: previewFilenameKeys.horizontalFilename,
            },
        },
    },
    FacebookDesktopRightColumn: {
        id: 6,
        icons: ['facebook-f'],
        text: 'desktop sidebar',
        type: previewImagesTypes.Square,
        name: previewFormatTypes.FacebookDesktopRightColumn,
        originType: previewOriginFormatTypes.RightColumn,
        variants: {
            [ThumbLayoutTypes.SideBySide]: {
                type: previewImagesTypes.Square,
                filenameKey: previewFilenameKeys.squareFilename,
            },
        },
    },
};

export const previewVideoFormats = {
    FacebookMobileFeed: {
        id: 1,
        icons: ['facebook-f'],
        text: 'mobile feed',
        type: previewVideoTypes.Square,
        name: previewFormatTypes.FacebookMobileFeed,
        originType: previewOriginFormatTypes.Feeds,
        filenameKey: previewFilenameKeys.squareFilename,
    },
    InstagramMobileFeed: {
        id: 2,
        icons: ['instagram'],
        text: 'mobile feed',
        type: previewVideoTypes.Square,
        name: previewFormatTypes.InstagramMobileFeed,
        originType: previewOriginFormatTypes.Feeds,
        filenameKey: previewFilenameKeys.squareFilename,
    },
    MetaReels: {
        id: 3,
        icons: ['facebook-f', 'instagram'],
        text: 'reels',
        type: previewVideoTypes.Vertical,
        name: previewFormatTypes.MetaReels,
        originType: previewOriginFormatTypes.StoriesAndReels,
        filenameKey: previewFilenameKeys.verticalFilename,
    },
    MetaStories: {
        id: 4,
        icons: ['facebook-f', 'instagram'],
        text: 'stories',
        type: previewVideoTypes.Vertical,
        name: previewFormatTypes.MetaStories,
        originType: previewOriginFormatTypes.StoriesAndReels,
        filenameKey: previewFilenameKeys.verticalFilename,
    },
};

export const mediaAssetsSuggestionsRejectionReasons = {
    doesNotFitCampaign: 'Doesn\'t fit my campaign',
    notRelevantForAd: 'Not relevant for my ad',
    poorVisualQuality: 'Poor visual quality',
    doesNotReflectOriginalCreative: 'Doesn\'t reflect my original creative',
    textOverlaysDoNotMeetExpectations: 'Text overlays don\'t meet expectations',
    other: 'My reason isn\'t listed',
};
